/**
 * TPO / Market Profile style settings — the contract between the Settings panel
 * and the renderer.
 *
 * Kept pure and separate from both so the panel can build controls off the same
 * type the draw reads, and so every colour/alpha decision is unit-testable
 * without a canvas.
 */

// Type-only, so this stays a one-way dependency with no runtime cycle: the
// bracket COUNTS are session arithmetic (tpoProfile), how they become letters is
// presentation (here).
import type { BracketScheme } from './tpoProfile'

/**
 * How a session's TPOs are laid out horizontally.
 *
 *  - `stacked` — every TPO in a row collapses to the left edge, so row length
 *    IS the count. The clean histogram silhouette.
 *  - `expanded` — each bracket keeps its own time column, so a TPO sits at the
 *    x-position of the bracket that made it and gaps appear where a bracket
 *    never traded that price. Shows how the session DEVELOPED rather than just
 *    where it ended up.
 */
export type TpoDisplay = 'stacked' | 'expanded'

/**
 * What a block's colour encodes.
 *
 *  - `time-gradient` — when it happened, interpolated along `blockColors`.
 *  - `value-area` — whether the row is inside the value area.
 *  - `single` — nothing; one flat colour.
 */
export type TpoColorBy = 'time-gradient' | 'value-area' | 'single'

export type TpoLineStyle = 'solid' | 'dashed' | 'dotted'

/** A level's appearance: colour, dash pattern, and whether it draws at all. */
export interface TpoLevelStyle {
  color: string
  lineStyle: TpoLineStyle
  visible: boolean
}

/**
 * How the height of a price row is decided.
 *
 *  - `auto` — derived from the instrument's recent session ranges, then snapped to
 *    a quotable increment.
 *  - `manual` — an explicit price width per row.
 *
 * Both keep the row size FIXED and let the row count fall out of each day's
 * range, which is a common charting model (a `ticks per row` parameter). The
 * count varying by day is correct: it is the truth about the day.
 */
export type TpoRowSizeMode = 'auto' | 'manual'

/**
 * Which bars the profile is COMPUTED from.
 *
 * Deliberately independent of the chart-wide extended-hours toggle, which decides
 * which bars are DISPLAYED. Market-profile traders routinely want to see the
 * pre-market run-up while measuring value from the regular session only — one
 * setting cannot serve both. With extended hours already off, `all` and
 * `regular` are the same thing.
 */
export type TpoProfileSession = 'all' | 'regular'

/** The volume profile drawn alongside a TPO. */
export interface TpoVolumeProfileSettings {
  visible: boolean
  /**
   * Share (%) of each session's span given to the volume band, leaving the rest
   * to the TPO. The TPO reclaims the full width when `visible` is false.
   */
  widthPct: number
  color: string
  /** Colour for the volume band's own point of control. */
  pocColor: string
}

export interface TpoSettings {
  rowSizeMode: TpoRowSizeMode
  /**
   * Price width of one row when `rowSizeMode` is `manual`.
   *
   * 0 means "inherit whatever auto derived", so switching to manual starts from
   * the current look instead of snapping to some arbitrary default that would be
   * absurd on one instrument or the other — $0.05 rows are meaningless on a
   * $65,000 index, and $1.00 rows are meaningless on a $3 small cap.
   */
  manualRowSize: number
  /**
   * Which bars the profile is computed from. No standalone UI since
   * 2026-08-01: the value is DERIVED at apply time from
   * `ChartSettings.extendedHours` (Session & data) — see useChartStyles'
   * TPO effect, which overrides whatever is stored here. The field stays so
   * the renderer keeps a single settings shape.
   */
  profileSession: TpoProfileSession
  /** Volume profile drawn beside the TPO. */
  volumeProfile: TpoVolumeProfileSettings
  display: TpoDisplay
  /** Draw the coloured blocks. Independent of `letters`. */
  blocks: boolean
  /** Draw the bracket letter (A, B, C…) in each block, where it fits. */
  letters: boolean
  colorBy: TpoColorBy
  /** Editable ramp stops for `time-gradient`, cool → warm. */
  blockColors: string[]
  /** Colour for `single` mode, and for out-of-value-area in `value-area` mode. */
  singleColor: string
  /** Opacity (%) of blocks OUTSIDE the value area. */
  outsideVaOpacity: number
  /** Opacity (%) applied to the whole profile, on top of the value-area tier. */
  profileOpacity: number
  poc: TpoLevelStyle
  valueArea: TpoLevelStyle
  /** First two brackets' range — the classic initial balance. */
  initialBalance: TpoLevelStyle
  /** Show the VAH / POC / VAL text labels. */
  levelLabels: boolean
  /** The close-price line drawn over the profiles. */
  line: TpoLevelStyle
  /**
   * Opacity (%) of the close-price line.
   *
   * Below 100 by default: at full strength a saturated line competes with the
   * blocks it is drawn over, and the blocks are the information.
   */
  lineOpacity: number
}

/**
 * Fill a partial/legacy `tpo` payload out to a complete settings object.
 *
 * Needed because chart settings are persisted and merged SHALLOWLY — layout code
 * does `{ ...DEFAULT_SETTINGS, ...saved }`, so a `tpo` object saved before a field
 * existed REPLACES the whole default rather than being topped up. Reading
 * `settings.tpo.volumeProfile.visible` off such an object threw.
 *
 * Nested objects are merged one level down for the same reason, so adding a field
 * to a `TpoLevelStyle` later cannot reintroduce the crash.
 */
export function normalizeTpoSettings(partial?: Partial<TpoSettings> | null): TpoSettings {
  const p = partial ?? {}
  const level = (
    value: Partial<TpoLevelStyle> | undefined,
    fallback: TpoLevelStyle,
  ): TpoLevelStyle => ({ ...fallback, ...(value ?? {}) })

  return {
    ...DEFAULT_TPO_SETTINGS,
    ...p,
    volumeProfile: { ...DEFAULT_TPO_SETTINGS.volumeProfile, ...(p.volumeProfile ?? {}) },
    poc: level(p.poc, DEFAULT_TPO_SETTINGS.poc),
    valueArea: level(p.valueArea, DEFAULT_TPO_SETTINGS.valueArea),
    initialBalance: level(p.initialBalance, DEFAULT_TPO_SETTINGS.initialBalance),
    line: level(p.line, DEFAULT_TPO_SETTINGS.line),
    // An array, so it falls back wholesale rather than merging index by index —
    // a half-overridden ramp would blend two unrelated palettes.
    blockColors: p.blockColors?.length ? p.blockColors : DEFAULT_TPO_SETTINGS.blockColors,
  }
}

/** Fallback when a caller hands us an empty ramp. */
const FALLBACK_COLOR = '#6E7480'

export const DEFAULT_TPO_SETTINGS: TpoSettings = {
  rowSizeMode: 'auto',
  // 0 = inherit the auto-derived size, so flipping to manual is a no-op until a
  // width is actually typed.
  manualRowSize: 0,
  // Every bar shown, matching what the chart displays. Opting into `regular` is
  // a deliberate analytical choice, not something to impose by default.
  profileSession: 'all',
  volumeProfile: {
    // Off by default: the TPO is the chart type the user selected, and it should
    // own its full width until the volume band is explicitly asked for.
    visible: false,
    widthPct: 30,
    color: '#64748B',
    pocColor: '#F5F7FA',
  },
  display: 'stacked',
  blocks: true,
  letters: true,
  colorBy: 'time-gradient',
  // Four stops, matching the reference panel's four swatches. Interpolation
  // between them still yields a smooth gradient.
  blockColors: ['#3B82F6', '#4ADE80', '#FACC15', '#EF4444'],
  singleColor: '#6E7480',
  outsideVaOpacity: 70,
  profileOpacity: 100,
  poc: { color: '#F5F7FA', lineStyle: 'solid', visible: true },
  valueArea: { color: '#4EA1FF', lineStyle: 'dashed', visible: true },
  // Ships hidden — the reference shows Initial Balance with a crossed-out eye.
  initialBalance: { color: '#F97316', lineStyle: 'dashed', visible: false },
  levelLabels: true,
  line: { color: '#9CA3AF', lineStyle: 'dashed', visible: true },
  lineOpacity: 60,
}

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
const LOWER = 'abcdefghijklmnopqrstuvwxyz'
const LETTERS = UPPER + LOWER

/**
 * Market Profile bracket letter: A–Z then a–z, wrapping afterwards.
 *
 * Wrapping (rather than running out) keeps a pathological configuration — a
 * 1-minute bracket over a full session is >52 brackets — rendering letters
 * instead of blanks or `undefined`.
 */
export function bracketLetter(bracket: number, scheme?: BracketScheme): string {
  const i = Number.isFinite(bracket) ? Math.floor(bracket) : 0
  if (!scheme) {
    const safe = i < 0 ? 0 : i
    return LETTERS[safe % LETTERS.length]
  }

  // Regular session: uppercase, A at the opening bracket.
  if (i >= 0 && i < scheme.rthBrackets) return UPPER[i % UPPER.length]

  /**
   * Extended hours: lowercase, on ONE sequence that reads forward across the
   * whole day — pre-market from the extended open, after-hours continuing where
   * pre-market stopped.
   *
   * Both stretches share the sequence so they can never reuse a letter: a 07:00
   * block and a 17:00 block have to stay distinguishable. Numbering pre-market
   * backwards from the bell instead (bracket -1 → 'a') would read right-to-left
   * against the chart and collide with after-hours at the same distance.
   */
  const ext = i < 0 ? scheme.preBrackets + i : scheme.preBrackets + (i - scheme.rthBrackets)
  // A bracket before the extended open still has to render something.
  const safe = ext < 0 ? ((ext % LOWER.length) + LOWER.length) % LOWER.length : ext % LOWER.length
  return LOWER[safe]
}

/** Linear blend of two #rrggbb colours. */
function mixHex(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16)
  const pb = parseInt(b.slice(1), 16)
  const chan = (shift: number) => {
    const ca = (pa >> shift) & 0xff
    const cb = (pb >> shift) & 0xff
    return Math.round(ca + (cb - ca) * t)
  }
  return `#${((chan(16) << 16) | (chan(8) << 8) | chan(0)).toString(16).padStart(6, '0')}`
}

/** Relative luminance of a #rrggbb colour, 0 (black) to 1 (white). */
function luminance(hex: string): number {
  const v = parseInt(hex.slice(1), 16)
  const r = ((v >> 16) & 0xff) / 255
  const g = ((v >> 8) & 0xff) / 255
  const b = (v & 0xff) / 255
  // Rec. 601 weighting — cheap and good enough to pick ink over a flat fill.
  return 0.299 * r + 0.587 * g + 0.114 * b
}

/** Dark ink on light fills, light ink on dark ones. */
const DARK_INK = '#0B0E14'
const LIGHT_INK = '#F5F7FA'

/**
 * Legible glyph colour for text drawn on `background`.
 *
 * Derived rather than hardcoded because the POC fill is user-configurable: the
 * default is near-white, so a light glyph is invisible on it, but a trader who
 * sets POC to a dark colour needs the opposite. Keying off luminance means the
 * letters stay readable either way without a second setting.
 */
export function inkOn(background: string): string {
  return luminance(background) > 0.55 ? DARK_INK : LIGHT_INK
}

/**
 * Colour for one block, given when it happened and whether its row is inside
 * the value area. Interpolates `blockColors` in time-gradient mode so the ramp
 * stays smooth however many stops the user configures.
 */
export function resolveBlockColor(
  settings: TpoSettings,
  bracket: number,
  bracketCount: number,
  inValueArea: boolean,
  /** Lowest bracket in the session (negative when pre-market is included), so
   *  the gradient's t=0 sits at the session's real start — see createRthBracketOf. */
  bracketMin: number = 0,
): string {
  if (settings.colorBy === 'single') return settings.singleColor
  if (settings.colorBy === 'value-area') {
    return inValueArea ? settings.valueArea.color : settings.singleColor
  }

  const stops = settings.blockColors
  if (stops.length === 0) return FALLBACK_COLOR
  if (stops.length === 1) return stops[0]
  if (bracketCount <= 1) return stops[0]

  const t = Math.min(Math.max((bracket - bracketMin) / (bracketCount - 1), 0), 1)
  const scaled = t * (stops.length - 1)
  const lo = Math.floor(scaled)
  const hi = Math.min(lo + 1, stops.length - 1)
  return mixHex(stops[lo], stops[hi], scaled - lo)
}

/** Percent (0–100) → a valid 0–1 alpha. */
export function pctToAlpha(value: number): number {
  if (!Number.isFinite(value)) return 1
  return Math.min(Math.max(value / 100, 0), 1)
}

/**
 * Alpha for one block: the value-area tier (full inside, `outsideVaOpacity`
 * outside) multiplied by the global `profileOpacity`.
 */
export function resolveBlockAlpha(settings: TpoSettings, inValueArea: boolean): number {
  const tier = inValueArea ? 1 : pctToAlpha(settings.outsideVaOpacity)
  return tier * pctToAlpha(settings.profileOpacity)
}
